import { useMutation, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { buildErrorMessage } from 'utils'
import { InformalFeedbackReview } from 'api/clients/InformalFeedbackRequestsClient'
import { Permission } from 'types'
import { informalFeedbackRequestsKeys } from '../informalFeedbackRequestsKeys'

interface Props {
  informalFeedbackRequestId: number
}

interface CreatedInformalFeedbackReview
  extends Omit<InformalFeedbackReview, 'request'>,
    Required<Pick<InformalFeedbackReview, 'request'>> {}

export interface Data {
  informal_feedback_review: CreatedInformalFeedbackReview
}

export interface Variables {
  reviewer_id: number
  required: boolean
  add_member: boolean
  permission?: Permission
  iteration?: number
  message?: string
}

export const useCreateReview = ({ informalFeedbackRequestId }: Props) => {
  const queryClient = useQueryClient()

  return useMutation<Data, unknown, Variables>(
    'createReview',
    async ({ reviewer_id, required, add_member, permission = Permission.Edit, iteration = 0, message }) => {
      const response = await sendToApi<{ informal_feedback_reviews: CreatedInformalFeedbackReview[] }>(
        `/informal_feedback_requests/${informalFeedbackRequestId}/reviews`,
        {
          method: 'POST',
          body: {
            reviewers: [{ id: reviewer_id, required: required }],
            add_members: add_member,
            permission,
            include_request: true,
            iteration,
            message,
          },
        },
      )
      if (response.type !== SUCCESS_RESPONSE_TYPE || response.data.informal_feedback_reviews.length === 0) {
        throw new Error(
          buildErrorMessage(response.data.errors) ||
            'An error occurred! Our team has been notified and we are looking into it.',
        )
      }
      return { informal_feedback_review: response.data.informal_feedback_reviews[0] }
    },
    {
      onSuccess: ({ informal_feedback_review }) => {
        queryClient.invalidateQueries(informalFeedbackRequestsKeys.detail(informalFeedbackRequestId))
        queryClient.invalidateQueries([
          informalFeedbackRequestsKeys.prefix,
          informal_feedback_review.request.article_id,
        ])
      },
    },
  )
}
